
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { shouldUsePinMode } from '@/utils/pinModeUtils';

interface MonthlyReportData {
  monthName: string;
  year: number;
  stats: {
    activeEmployees: number;
    totalHours: number;
    revenue: number;
    productivity: number;
    avgHoursPerDay: number;
  };
  weeklyTrend: Array<{ week: string; hours: number }>;
  employeePerformance: Array<{ name: string; hours: number }>;
  projectDistribution: Array<{ name: string; hours: number; percentage: number }>;
  dailyAverage: Array<{ day: string; hours: number }>;
  topPerformers: Array<{ name: string; hours: number; projects: number }>;
  rawEntries: RawEntry[];
  profilesList: Array<{ id: string; name: string }>;
  projectsList: Array<{ id: string; name: string }>;
}

export interface RawEntry {
  id: string;
  user_id: string;
  project_id: string;
  start_time: string;
  duration_minutes: number | null;
  description: string | null;
  status: string;
  is_billable: boolean;
  is_invoiced: boolean;
  hourly_rate: number | null;
  employee_name: string;
  project_name: string;
}

interface TimeEntry {
  user_id: string;
  project_id: string;
  start_time: string;
  duration_minutes: number | null;
  hourly_rate?: number | null;
  projects?: { name: string } | null;
}

export const useMonthlyReportData = (customStart?: Date, customEnd?: Date) => {
  const [reportData, setReportData] = useState<MonthlyReportData>({
    monthName: new Date().toLocaleString('fr-FR', { month: 'long' }).replace(/^./, c => c.toUpperCase()),
    year: new Date().getFullYear(),
    stats: { activeEmployees: 0, totalHours: 0, revenue: 0, productivity: 0, avgHoursPerDay: 0 },
    weeklyTrend: [],
    employeePerformance: [],
    projectDistribution: [],
    dailyAverage: [],
    topPerformers: [],
    rawEntries: [],
    profilesList: [],
    projectsList: [],
  });
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const monthNames = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
  ];

  const fetchMonthlyReportData = useCallback(async () => {
    try {
      setLoading(true);

      let startOfMonth: Date;
      let endOfMonth: Date;

      if (customStart && customEnd) {
        startOfMonth = new Date(customStart);
        startOfMonth.setHours(0, 0, 0, 0);
        endOfMonth = new Date(customEnd);
        endOfMonth.setHours(23, 59, 59, 999);
      } else {
        const now = new Date();
        startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        endOfMonth.setHours(23, 59, 59, 999);
      }

      const usePinMode = shouldUsePinMode();
      let timeEntries: TimeEntry[] = [];
      let activeEmployeesCount = 0;
      let profilesMap = new Map<string, { first_name: string; last_name: string }>();
      let projectsMap = new Map<string, string>();

      if (usePinMode) {
        const pin = localStorage.getItem('manager_pin') || localStorage.getItem('employee_pin') || '';

        const [profilesRes, projectsRes, entriesRes] = await Promise.all([
          supabase.rpc('pin_list_profiles', { p_pin: pin }),
          supabase.rpc('pin_list_projects', { p_pin: pin }),
          supabase.rpc('pin_list_time_entries_range', {
            p_pin: pin,
            p_start: startOfMonth.toISOString(),
            p_end: endOfMonth.toISOString(),
            p_limit: 10000,
            p_offset: 0,
            p_order: 'asc',
          }),
        ]);

        if (profilesRes.error) throw profilesRes.error;
        if (projectsRes.error) throw projectsRes.error;
        if (entriesRes.error) throw entriesRes.error;

        const profiles = profilesRes.data || [];
        profiles.forEach((p: any) => profilesMap.set(p.id, { first_name: p.first_name, last_name: p.last_name }));

        const projects = projectsRes.data || [];
        projects.forEach((p: any) => projectsMap.set(p.id, p.name));

        timeEntries = (entriesRes.data || []).map((e: any) => ({
          ...e,
          projects: e.project_id ? { name: projectsMap.get(e.project_id) || 'Sans projet' } : null,
        }));
      } else {
        const [entriesRes, profilesRes] = await Promise.all([
          supabase.from('time_entries').select('duration_minutes, start_time, hourly_rate, user_id, projects(name)')
            .gte('start_time', startOfMonth.toISOString())
            .lte('start_time', endOfMonth.toISOString()),
          supabase.from('profiles').select('id, first_name, last_name'),
        ]);

        if (entriesRes.error) throw entriesRes.error;
        if (profilesRes.error) throw profilesRes.error;

        timeEntries = (entriesRes.data || []) as TimeEntry[];
        (profilesRes.data || []).forEach((p: any) => profilesMap.set(p.id, { first_name: p.first_name, last_name: p.last_name }));
      }

      // Employés actifs = ceux qui ont des entrées sur la période
      const uniqueUserIds = new Set(timeEntries.map(e => e.user_id));
      activeEmployeesCount = uniqueUserIds.size;

      const totalMinutes = timeEntries.reduce((sum, e) => sum + (e.duration_minutes || 0), 0);
      const totalHours = Math.round((totalMinutes / 60) * 10) / 10;

      const revenue = timeEntries.reduce((sum, e) => {
        const hours = (e.duration_minutes || 0) / 60;
        const rate = e.hourly_rate || 25;
        return sum + (hours * rate);
      }, 0);

      // Tendance hebdomadaire
      const weeklyTrend: Array<{ week: string; hours: number }> = [];
      const totalDays = Math.round((endOfMonth.getTime() - startOfMonth.getTime()) / (1000 * 60 * 60 * 24)) + 1;
      const weeksInRange = Math.ceil(totalDays / 7);
      for (let week = 0; week < weeksInRange; week++) {
        const weekStart = new Date(startOfMonth);
        weekStart.setDate(weekStart.getDate() + (week * 7));
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekEnd.getDate() + 6);
        if (weekEnd > endOfMonth) weekEnd.setTime(endOfMonth.getTime());

        const weekEntries = timeEntries.filter(e => {
          const d = new Date(e.start_time);
          return d >= weekStart && d <= weekEnd;
        });
        const weekMinutes = weekEntries.reduce((sum, e) => sum + (e.duration_minutes || 0), 0);
        weeklyTrend.push({ week: `S${week + 1}`, hours: Math.round((weekMinutes / 60) * 10) / 10 });
      }

      // Performance par employé
      const employeeStats = new Map<string, number>();
      const employeeProjects = new Map<string, Set<string>>();
      timeEntries.forEach(e => {
        const profile = profilesMap.get(e.user_id);
        if (profile) {
          const name = `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || 'N/A';
          employeeStats.set(name, (employeeStats.get(name) || 0) + (e.duration_minutes || 0));
          const projects = employeeProjects.get(name) || new Set();
          projects.add(e.projects?.name ?? 'Sans projet');
          employeeProjects.set(name, projects);
        }
      });

      const employeePerformance = Array.from(employeeStats.entries())
        .map(([name, min]) => ({ name, hours: Math.round((min / 60) * 10) / 10 }))
        .sort((a, b) => b.hours - a.hours)
        .slice(0, 8);

      const topPerformers = Array.from(employeeStats.entries())
        .map(([name, min]) => ({
          name,
          hours: Math.round((min / 60) * 10) / 10,
          projects: employeeProjects.get(name)?.size || 0,
        }))
        .sort((a, b) => b.hours - a.hours)
        .slice(0, 3);

      // Distribution par projet
      const projectStats = new Map<string, number>();
      timeEntries.forEach(e => {
        const pName = e.projects?.name ?? 'Sans projet';
        projectStats.set(pName, (projectStats.get(pName) || 0) + (e.duration_minutes || 0));
      });

      const projectDistribution = Array.from(projectStats.entries())
        .map(([name, min]) => {
          const hours = Math.round((min / 60) * 10) / 10;
          const percentage = totalHours > 0 ? Math.round((hours / totalHours) * 100) : 0;
          return { name, hours, percentage };
        })
        .sort((a, b) => b.hours - a.hours);

      // Moyenne quotidienne par jour de la semaine
      const dailyStats = new Map<string, { total: number; count: number }>();
      const dayNames = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
      timeEntries.forEach(e => {
        const d = new Date(e.start_time);
        const dow = d.getDay();
        const dayName = dayNames[dow === 0 ? 6 : dow - 1];
        const current = dailyStats.get(dayName) || { total: 0, count: 0 };
        current.total += (e.duration_minutes || 0);
        current.count += 1;
        dailyStats.set(dayName, current);
      });

      const dailyAverage = dayNames.map(day => {
        const stats = dailyStats.get(day) || { total: 0, count: 1 };
        return { day, hours: Math.round(((stats.total / 60) / Math.max(stats.count, 1)) * 10) / 10 };
      });

      // Entrées brutes enrichies pour le tableau
      const rawEntries: RawEntry[] = timeEntries.map((e: any) => ({
        id: e.id || '',
        user_id: e.user_id,
        project_id: e.project_id,
        start_time: e.start_time,
        duration_minutes: e.duration_minutes,
        description: e.description || null,
        status: e.status || 'closed',
        is_billable: e.is_billable ?? true,
        is_invoiced: e.is_invoiced ?? false,
        hourly_rate: e.hourly_rate || null,
        employee_name: (() => {
          const p = profilesMap.get(e.user_id);
          return p ? `${p.first_name} ${p.last_name}`.trim() : 'N/A';
        })(),
        project_name: e.projects?.name ?? 'Sans projet',
      }));

      const profilesList = Array.from(profilesMap.entries()).map(([id, p]) => ({
        id,
        name: `${p.first_name} ${p.last_name}`.trim(),
      }));

      const projectsList = Array.from(projectsMap.entries()).map(([id, name]) => ({ id, name }));

      setReportData({
        monthName: monthNames[startOfMonth.getMonth()],
        year: startOfMonth.getFullYear(),
        stats: {
          activeEmployees: activeEmployeesCount,
          totalHours,
          revenue: Math.round(revenue),
          productivity: totalHours > 0 ? Math.min(100, Math.round((totalHours / (activeEmployeesCount * 160)) * 100)) : 0,
          avgHoursPerDay: totalDays > 0 ? Math.round((totalHours / totalDays) * 10) / 10 : 0,
        },
        weeklyTrend,
        employeePerformance,
        projectDistribution,
        dailyAverage,
        topPerformers,
        rawEntries,
        profilesList,
        projectsList,
      });
    } catch (error) {
      console.error('Erreur lors du chargement du rapport mensuel:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger le rapport mensuel",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [customStart?.getTime(), customEnd?.getTime()]);

  useEffect(() => {
    fetchMonthlyReportData();
  }, [fetchMonthlyReportData]);

  return { reportData, loading, refetch: fetchMonthlyReportData };
};
